import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ExternalLinkIcon } from 'lucide-react';
import {
  BackLink,
  EmptyState,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  StatTile,
  TrendChart,
  type TrendChartVariant,
  type TrendOverlay,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  CHART_VARIANTS,
  INTERVAL_LABELS,
  LIVE_REFRESH_MS,
  RANGES,
  TREND_WINDOWS,
  periodLabel,
  rangeWindow,
  toTrendOverlays,
  toTrendPoints,
  type RangeKey,
  type TrendWindowKey,
} from '@/lib/report-window';
import { TogglePill } from '@/components/report-bits';
import {
  publicPageUrl,
  readPageDetail,
  readPageLeads,
  readPageQueries,
  readPageSeries,
  type PageLeadsStats,
  type PageQueryRow,
  type PageSeriesPoint,
  type PageSeriesStats,
} from '@/lib/pages';
import { useLocalSort } from '@/lib/use-local-sort';
import { InfoTip, METRIC_INFO, METRIC_LABEL } from '@/components/metric-info';
import { pctChange, TrafficSourcesPanel } from '@/components/traffic-sources';
import { QueryTrendDrawer } from '@/components/query-trend-drawer';
import { QUERY_ACCESSORS, QueryTable } from '@/components/query-table';
import { placesSuffix, positionDelta, positionHint } from '@/components/page-trend';
import { STATS_INTERVAL, type StatsInterval } from '@/lib/leads';
import { useIntervalChoice } from '@/lib/use-interval-choice';

/**
 * The per-page range presets reach further than the site-wide screen: one
 * page's rows are cheap, and GA4 traffic history goes back to mid-2023.
 * Search metrics only exist for the last ~16 months (GSC retention).
 */
const DETAIL_RANGE_KEYS: readonly RangeKey[] = ['7d', '30d', '90d', '12m', '24m', '36m'];

/** What the chart can show; each stat tile picks its own. */
const TRAFFIC_METRICS = {
  clicks: { label: 'Clicks', unit: 'clicks' },
  impressions: { label: 'Impressions', unit: 'impressions' },
  ctr: { label: 'CTR', unit: 'CTR' },
  position: { label: 'Avg position', unit: 'avg position' },
  sessions: { label: 'Sessions', unit: 'sessions' },
} as const;
type TrafficMetric = keyof typeof TRAFFIC_METRICS;

function metricValue(point: Omit<PageSeriesPoint, 'period'>, metric: TrafficMetric): number {
  switch (metric) {
    case 'clicks':
      return point.clicks;
    case 'impressions':
      return point.impressions;
    case 'sessions':
      return point.sessions;
    case 'ctr':
      return point.ctr === null ? 0 : Math.round(point.ctr * 10000) / 100;
    case 'position':
      return positionValue(point);
    default: {
      const exhaustive: never = metric;
      return exhaustive;
    }
  }
}

function positionValue(point: Omit<PageSeriesPoint, 'period'>): number {
  return point.avgPosition === null ? 0 : Math.round(point.avgPosition * 10) / 10;
}

function ctrLabel(ctr: number | null): string {
  return ctr === null ? '—' : `${(ctr * 100).toFixed(1)}%`;
}

function positionLabel(position: number | null): string {
  return position === null ? '—' : position.toFixed(1);
}

/** Live-lookup panels re-ask Google at most every five minutes. */
const LIVE_LOOKUP_STALE_MS = 5 * 60 * 1000;

function compactCount(value: number): string {
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

/** Last year's values as an overlay for the given accessor. */
function yoyOverlay(
  stats: PageSeriesStats,
  value: (point: Omit<PageSeriesPoint, 'period'>) => number,
): TrendOverlay | undefined {
  if (!stats.yoy) return undefined;
  return {
    key: 'yoy',
    label: 'Same period last year',
    values: stats.yoy.map(value).slice(stats.range.leadIn),
    tone: 1,
  };
}

/**
 * One page's SEO/traffic history (admin-only): search position over time
 * front and center, with clicks/impressions/CTR/sessions beside it — the
 * screen to watch while optimizing a page and waiting for rankings to move.
 *
 * @returns The page detail screen.
 */
export function PageDetailPage(): React.ReactElement {
  const [params] = useSearchParams();
  const path = params.get('path') ?? '';
  const [range, setRange] = useState<RangeKey>('30d');
  const [variant, setVariant] = useState<TrendChartVariant>('bar');
  const [metric, setMetric] = useState<TrafficMetric>('clicks');
  const [trends, setTrends] = useState<ReadonlySet<TrendWindowKey>>(() => new Set(['short']));
  const [yoy, setYoy] = useState(false);
  const statsWindow = useMemo(() => rangeWindow(range), [range]);
  const [effectiveInterval, setIntervalChoice] = useIntervalChoice(RANGES[range].interval);
  const leadIn = Math.max(...TREND_WINDOWS[effectiveInterval].map((window) => window.buckets)) - 1;

  const detail = useQuery({
    queryKey: ['page-detail', path],
    queryFn: ({ signal }) => readPageDetail(path, signal),
    enabled: path !== '',
  });
  const stats = useQuery({
    queryKey: ['page-series', path, statsWindow.from, statsWindow.to, effectiveInterval, leadIn, yoy],
    queryFn: ({ signal }) =>
      readPageSeries(
        {
          from: statsWindow.from,
          to: statsWindow.to,
          interval: effectiveInterval,
          leadIn,
          path,
          ...(yoy ? { yoy: '1' as const } : {}),
        },
        signal,
      ),
    enabled: path !== '',
    refetchInterval: LIVE_REFRESH_MS,
    placeholderData: keepPreviousData,
  });

  const queries = useQuery({
    queryKey: ['page-queries', path, statsWindow.from, statsWindow.to],
    queryFn: ({ signal }) =>
      readPageQueries({ from: statsWindow.from, to: statsWindow.to, path }, signal),
    enabled: path !== '',
    staleTime: LIVE_LOOKUP_STALE_MS,
  });

  const leads = useQuery({
    queryKey: ['page-leads', path, statsWindow.from, statsWindow.to, effectiveInterval],
    queryFn: ({ signal }) =>
      readPageLeads(
        { from: statsWindow.from, to: statsWindow.to, path, interval: effectiveInterval },
        signal,
      ),
    enabled: path !== '',
    refetchInterval: LIVE_REFRESH_MS,
    placeholderData: keepPreviousData,
  });

  if (path === '') {
    return (
      <section className="animate-portal-rise">
        <BackLink to="/analytics/pages">Pages</BackLink>
        <ErrorNotice title="No page selected" message="Pick a page from the Pages table." />
      </section>
    );
  }
  if (stats.isPending) return <LoadingState label="Loading page history…" />;
  if (stats.isError) {
    return (
      <section className="animate-portal-rise">
        <BackLink to="/analytics/pages">Pages</BackLink>
        <ErrorNotice title="Page history unavailable" message={errorMessage(stats.error)} />
      </section>
    );
  }

  const data = stats.data;
  const totals = data.totals;
  const rangeLabel = RANGES[range].label;
  const bucketed = { range: data.range, series: data.series, segments: null, yoy: null };
  const hasSearchData = totals.impressions > 0 || totals.previous.impressions > 0;

  return (
    <section className="animate-portal-rise">
      <BackLink to="/analytics/pages">Pages</BackLink>
      <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
        <PageHeading
          eyebrow="PAGE ANALYTICS"
          // The title is the raw URL path — a long, data-derived string that
          // stacks into a tall multi-line block at the 36px page size on a
          // phone; compact (28px) is the size meant for exactly this.
          size="compact"
          title={path}
          subtitle={detail.data?.title || 'Search position and traffic over time for this page.'}
        />
        <div className="flex items-center gap-2">
          {detail.data?.template ? <Pill tone="neutral">{detail.data.template}</Pill> : null}
          <a
            href={publicPageUrl(path)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-portal-small text-brand"
          >
            <ExternalLinkIcon className="size-4" aria-hidden="true" />
            View live page
          </a>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        {/* min-w-0 lets this group shrink below its controls' natural width on a
            phone, so each SegmentedControl's own overflow-x scroll engages
            instead of the group forcing the whole page wider than the screen. */}
        <div className="flex min-w-0 flex-wrap gap-3">
          <SegmentedControl<RangeKey>
            label="Range"
            value={range}
            onChange={(next) => {
              setRange(next);
            }}
            options={DETAIL_RANGE_KEYS.map((key) => ({ key, label: RANGES[key].label, shortLabel: RANGES[key].shortLabel }))}
          />
          <SegmentedControl<StatsInterval>
            label="Interval"
            value={effectiveInterval}
            onChange={setIntervalChoice}
            options={(Object.keys(INTERVAL_LABELS) as StatsInterval[]).map((key) => ({
              key,
              label: INTERVAL_LABELS[key],
            }))}
          />
        </div>
        <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Trend lines">
          {TREND_WINDOWS[effectiveInterval].map((window) => (
            <TogglePill
              key={window.key}
              on={trends.has(window.key)}
              onToggle={() =>
                setTrends((previous) => {
                  const next = new Set(previous);
                  if (next.has(window.key)) next.delete(window.key);
                  else next.add(window.key);
                  return next;
                })
              }
            >
              {window.label}
            </TogglePill>
          ))}
          <TogglePill on={yoy} onToggle={() => setYoy((previous) => !previous)}>
            Last year
          </TogglePill>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
        <StatTile
          label="Clicks"
          onSelect={() => setMetric('clicks')}
          selected={metric === 'clicks'}
          labelAccessory={<InfoTip label="clicks" text={METRIC_INFO.clicks} />}
          value={totals.clicks.toLocaleString()}
          changePct={totals.changePct}
          hint={`vs ${totals.previous.clicks.toLocaleString()} the previous ${rangeLabel}`}
        />
        <StatTile
          label="Impressions"
          onSelect={() => setMetric('impressions')}
          selected={metric === 'impressions'}
          labelAccessory={<InfoTip label="impressions" text={METRIC_INFO.impressions} />}
          value={compactCount(totals.impressions)}
          changePct={pctChange(totals.impressions, totals.previous.impressions)}
          hint={`vs ${compactCount(totals.previous.impressions)} the previous ${rangeLabel}`}
        />
        <StatTile
          label="CTR"
          onSelect={() => setMetric('ctr')}
          selected={metric === 'ctr'}
          labelAccessory={<InfoTip label="CTR" text={METRIC_INFO.ctr} />}
          value={ctrLabel(totals.ctr)}
          hint={`vs ${ctrLabel(totals.previous.ctr)} the previous ${rangeLabel}`}
        />
        <StatTile
          label="Avg position"
          onSelect={() => setMetric('position')}
          selected={metric === 'position'}
          labelAccessory={<InfoTip label="average position" text={METRIC_INFO.position} />}
          value={positionLabel(totals.avgPosition)}
          changePct={positionDelta(totals.avgPosition, totals.previous.avgPosition)}
          changeSuffix={placesSuffix(positionDelta(totals.avgPosition, totals.previous.avgPosition))}
          downIsGood
          hint={positionHint(totals.avgPosition, totals.previous.avgPosition)}
        />
        <StatTile
          label="Sessions"
          onSelect={() => setMetric('sessions')}
          selected={metric === 'sessions'}
          labelAccessory={<InfoTip label="sessions" text={METRIC_INFO.sessions} />}
          value={totals.sessions.toLocaleString()}
          changePct={pctChange(totals.sessions, totals.previous.sessions)}
          hint={`vs ${totals.previous.sessions.toLocaleString()} the previous ${rangeLabel}`}
        />
      </div>

      <Panel className="mt-4 p-5">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-portal-card-title font-bold text-portal-ink">{TRAFFIC_METRICS[metric].label} over time</h2>
            <p className="text-portal-micro text-portal-muted">
              {INTERVAL_LABELS[effectiveInterval]} · {periodLabel(data.range.from, STATS_INTERVAL.day)} –{' '}
              {periodLabel(data.range.to, STATS_INTERVAL.day)}
              {metric === 'position' ? ' · lower is better' : ''} · click a tile above to switch
            </p>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <SegmentedControl<TrafficMetric>
              label="Metric"
              value={metric}
              onChange={setMetric}
              options={(Object.keys(TRAFFIC_METRICS) as TrafficMetric[]).map((key) => ({
                key,
                label: TRAFFIC_METRICS[key].label,
              }))}
            />
            {/* Position always draws as a line, so the switch only shows where it does something. */}
            {metric !== 'position' ? (
              <SegmentedControl<TrendChartVariant>
                label="Chart type"
                value={variant}
                onChange={setVariant}
                options={CHART_VARIANTS.map((option) => ({ key: option.key, label: option.label }))}
              />
            ) : null}
          </div>
        </div>
        {metric !== 'sessions' && !hasSearchData ? (
          <EmptyState
            title="No search data for this page"
            message="Google Search Console has no impressions here. Search metrics reach back about 16 months at most."
          />
        ) : totals.clicks === 0 && totals.sessions === 0 && totals.impressions === 0 ? (
          <EmptyState title="No traffic in this range" message="Try a wider range." />
        ) : (
          <TrendChart
            points={toTrendPoints(
              bucketed,
              (point) => metricValue(point, metric),
              (point) => {
                const details: string[] = [];
                if (metric !== 'clicks' && point.clicks > 0) details.push(`${point.clicks.toLocaleString()} clicks`);
                if (metric !== 'ctr' && point.ctr !== null) details.push(`${ctrLabel(point.ctr)} CTR`);
                if (metric !== 'impressions' && point.impressions > 0) details.push(`${point.impressions.toLocaleString()} impressions`);
                if (metric !== 'sessions' && point.sessions > 0) {
                  details.push(`${point.sessions.toLocaleString()} sessions`);
                }
                return details;
              },
            )}
            overlays={[
              ...toTrendOverlays(
                bucketed,
                data.series.map((point) => metricValue(point, metric)),
                trends,
              ),
              ...(() => {
                const overlay = yoyOverlay(data, (point) => metricValue(point, metric));
                return overlay ? [overlay] : [];
              })(),
            ]}
            // Position reads as a line: bars would make a worse (higher) rank look bigger.
            variant={metric === 'position' ? 'line' : variant}
            title={TRAFFIC_METRICS[metric].label}
            unit={TRAFFIC_METRICS[metric].unit}
            formatValue={metric === 'ctr' ? (value) => `${value}%` : metric === 'position' ? (value) => value.toFixed(1) : compactCount}
          />
        )}
      </Panel>

      <LeadsPanel query={leads} rangeLabel={rangeLabel} interval={effectiveInterval} sessions={totals.sessions} />

      <TrafficSourcesPanel
        hint="Where this page's sessions came from (GA4, live) — spot the channel or referrer a drop came from."
        rangeLabel={rangeLabel}
        from={statsWindow.from}
        to={statsWindow.to}
        interval={effectiveInterval}
        path={path}
      />

      <div className="mt-4">
        {queries.isPending ? (
          <Panel className="p-5">
            <LoadingState label="Asking Search Console…" />
          </Panel>
        ) : queries.isError ? (
          <ErrorNotice title="Search queries unavailable" message={errorMessage(queries.error)} />
        ) : (
          <QueriesPanel rows={queries.data.queries} rangeLabel={rangeLabel} path={path} window={statsWindow} />
        )}
      </div>
    </section>
  );
}

/**
 * How this page turns traffic into leads: on-page conversions (the form was
 * submitted here) and assisted (the visit started here). Salesforce is the
 * same-day source; assisted (first-touch) is sparse, so it undercounts.
 */
function LeadsPanel({
  query,
  rangeLabel,
  interval,
  sessions,
}: {
  readonly query: UseQueryResult<PageLeadsStats>;
  readonly rangeLabel: string;
  readonly interval: StatsInterval;
  /** Page sessions in the window, for the conversion rate (leads ÷ sessions). */
  readonly sessions: number;
}): React.ReactElement {
  return (
    <Panel className="mt-4 p-5">
      <div className="mb-3">
        <h2 className="text-portal-card-title font-bold text-portal-ink">Leads</h2>
        <p className="text-portal-micro text-portal-muted">
          How this page converts its traffic — form submissions here (on page) and visits that started here (assisted).
        </p>
      </div>
      {query.isPending ? (
        <LoadingState label="Loading leads…" />
      ) : query.isError ? (
        <ErrorNotice title="Leads unavailable" message={errorMessage(query.error)} />
      ) : (
        (() => {
          const data = query.data;
          const { onPage, assisted } = data.totals;
          return (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
                <StatTile
                  label="Leads on page"
                  labelAccessory={<InfoTip label="leads" text={METRIC_INFO.leads} />}
                  value={onPage.toLocaleString()}
                  hint={`converted on this page · ${rangeLabel}`}
                />
                <StatTile
                  label={METRIC_LABEL.conversionRate}
                  labelAccessory={<InfoTip label="conversion rate" text={METRIC_INFO.leadRate} />}
                  value={sessions > 0 ? `${((onPage / sessions) * 100).toFixed(2)}%` : '—'}
                  hint={`leads ÷ ${sessions.toLocaleString()} sessions · ${rangeLabel}`}
                />
                <StatTile
                  label="Assisted"
                  labelAccessory={<InfoTip label="assisted leads" text={METRIC_INFO.assisted} />}
                  value={assisted.toLocaleString()}
                  hint={`first visit started here · ${rangeLabel}`}
                />
              </div>
              {onPage === 0 && assisted === 0 ? (
                <div className="mt-4">
                  <EmptyState
                    title="No leads in this range"
                    message="No form submissions are attributed to this page yet. Try a wider range."
                  />
                </div>
              ) : (
                <div className="mt-4">
                  <TrendChart
                    points={data.series.map((point) => ({
                      key: point.day,
                      label: periodLabel(point.day, interval),
                      value: point.onPage,
                      details: point.assisted > 0 ? [`${point.assisted.toLocaleString()} assisted`] : [],
                    }))}
                    overlays={[
                      {
                        key: 'assisted',
                        label: 'Assisted',
                        values: data.series.map((point) => point.assisted),
                        tone: 1,
                      },
                    ]}
                    variant="bar"
                    title="Leads on page"
                    unit="leads"
                    formatValue={(value) => value.toLocaleString()}
                  />
                </div>
              )}
            </>
          );
        })()
      )}
    </Panel>
  );
}

/** Which searches drove this page, with previous-window deltas — the keyword drop-off view. A row opens the query's own trend. */
function QueriesPanel({
  rows,
  rangeLabel,
  path,
  window,
}: {
  readonly rows: readonly PageQueryRow[];
  readonly rangeLabel: string;
  readonly path: string;
  readonly window: { readonly from: string; readonly to: string };
}): React.ReactElement {
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const { sorted, sort, onSort } = useLocalSort(rows, QUERY_ACCESSORS, {
    key: 'clicks',
    direction: 'desc',
  });
  const shown =
    filter.trim() === ''
      ? sorted
      : sorted.filter((row) => row.query.toLowerCase().includes(filter.trim().toLowerCase()));
  return (
    <Panel>
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-5 pb-4">
        <div>
          <h3 className="text-portal-card-title font-bold text-portal-ink">Top search queries</h3>
          <p className="text-portal-micro text-portal-muted">
            What people searched to find this page (Search Console, live) — change is vs the previous {rangeLabel}. Click a query for its day-by-day trend.
          </p>
        </div>
        <SearchInput
          label="Filter queries"
          placeholder="Query…"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          className="w-full sm:w-64"
        />
      </div>
      {shown.length === 0 ? (
        <p className="px-5 pb-5 text-portal-micro text-portal-muted">
          {filter ? 'No queries match.' : 'No search queries in this range.'}
        </p>
      ) : (
        <div className="max-h-[480px] overflow-y-auto rounded-b-[8px]">
          <QueryTable rows={shown} sort={sort} onSort={onSort} onSelect={setSelected} />
        </div>
      )}
      <QueryTrendDrawer path={path} query={selected} window={window} rangeLabel={rangeLabel} onClose={() => setSelected(null)} />
    </Panel>
  );
}
